import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auth, googleProvider } from './firebase';
import { closeAdminSession, loadOrCreateAdminAccess, setAdminSessionId } from './services/adminApi';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const refreshAccess = useCallback(async () => {
    const result = await loadOrCreateAdminAccess();
    setAccess(result.access || result);
    return result.access || result;
  }, []);

  useEffect(() => auth.onAuthStateChanged(async (user) => {
    setCurrentUser(user);
    setLoading(true);
    setAuthError(null);
    if (!user) {
      setAccess(null);
      setAdminSessionId('');
      setLoading(false);
      return;
    }
    try {
      await refreshAccess();
    } catch (error) {
      setAccess(null);
      setAuthError(error);
    } finally {
      setLoading(false);
    }
  }), [refreshAccess]);

  const login = (email, password) => auth.signInWithEmailAndPassword(email, password);
  const loginWithGoogle = () => auth.signInWithPopup(googleProvider);
  const logout = async () => {
    await closeAdminSession().catch(() => setAdminSessionId(''));
    return auth.signOut();
  };
  const hasPermission = useCallback((permission) => Boolean(access?.isSuperadmin || access?.permissions?.includes(permission)), [access]);
  const hasSystemScope = useCallback((system) => Boolean(access?.isSuperadmin || access?.scopes?.systems?.includes('*') || access?.scopes?.systems?.includes(system)), [access]);

  const value = useMemo(() => ({
    currentUser,
    access,
    userRole: access?.isSuperadmin ? 'superadmin' : (access?.active ? 'admin' : null),
    permissions: access?.permissions || [],
    scopes: access?.scopes || { systems: [], branches: [] },
    isSuperadmin: Boolean(access?.isSuperadmin),
    loading,
    authError,
    login,
    loginWithGoogle,
    logout,
    hasPermission,
    hasSystemScope,
    refreshAccess,
  }), [currentUser, access, loading, authError, hasPermission, hasSystemScope, refreshAccess]);

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
